import utils from 'utils/utils';
import device from 'utils/device';
import logger from 'utils/logger';
import { Model } from 'utils/model';
import autoplay from './config/autoplay';
import appnexus from './ads/appnexus';
import preventAdSeeking from './ads/prevent-seeking';
import { onAdEventImpression } from './ads/relevant-yield';

import { youboraSdk } from './youbora';
import svpSkin, { skinLoader } from './skin';
import statusCode from './config/status-codes';

import type { ConfigModel } from './config';
import type { StreamModel } from 'player/model/stream';
import type { AssetModel } from 'player/model/asset';

/**
 * Indicates if adblock has been detected
 */
let adBlockDetected = false;

fetch('https://ib.adnxs.com/', { method: 'HEAD', mode: 'no-cors' }).catch(
    () => {
        adBlockDetected = true;
    },
);

function getAdPosition(slotId: 'pre' | 'mid' | 'post') {
    const slotKeys = {
        pre: 'preroll',
        mid: 'midroll',
        post: 'postroll',
    } as const;

    return slotKeys[slotId] ?? slotId;
}

export function normalizeAspectRatio(aspectRatio = 16 / 9): number {
    return parseFloat(aspectRatio.toFixed(2));
}

export function deriveAspectRatioFromAd(
    data: jwplayer.AdImpressionData,
): number | undefined {
    let width = 16;
    let height = 9;

    try {
        switch (data.client) {
            case 'googima': {
                const adData = data.ima?.ad?.data;
                width = adData?.vastMediaWidth ?? width;
                height = adData?.vastMediaHeight ?? height;
                break;
            }
            case 'vast': {
                const node = data.response?.querySelector(
                    `Ad:nth-child(${
                        data.sequence ?? 1
                    }) MediaFile:last-of-type`,
                );
                width =
                    parseInt(node?.getAttribute('width') ?? '', 10) || width;
                height =
                    parseInt(node?.getAttribute('height') ?? '', 10) || height;

                break;
            }
            default:
                break;
        }

        return normalizeAspectRatio(width / height);
    } catch {
        // noop
    }

    return undefined;
}

/**
 * Parse adresponse for each adslot
 */
function parseAdData(data: jwplayer.AdImpressionData) {
    const { sequence } = data;
    const xml = data && data.response;

    let source = 'WRAPPER';
    let adSelector = 'Ad';
    let adData: Element | null = null;

    if (xml) {
        // only when additional data is given we set source to appnexus
        if (xml.URL === data.tag) {
            source = 'INLINE';
            adSelector = `Ad[sequence="${sequence}"]`;
        }

        const adElements = Array.prototype.slice.call(
            xml.querySelectorAll(adSelector),
        ) as Element[];
        // get first element if available
        adData = adElements.length > 0 ? (adElements[0] ?? null) : null;
    }

    return {
        sequence: sequence,
        count: data.podcount,
        raw: adData,
        source: source,
        meta: data,
    };
}

export class PlayerModel extends Model {
    player: jwplayer.JWPlayer | null = null;

    stream: StreamModel | null = null;

    config: ConfigModel;

    isInitialized: boolean = false;

    /**
     * Indicates whenever stream was stopped
     */
    isStopped: boolean = false;

    /**
     * Current playlist item
     */
    playlistItem: jwplayer.PlaylistItem | null = null;

    /**
     * Current time holder for seek and seeked events
     * Fix for JW getPosition bug
     */
    currentTime: number | null = null;

    /**
     * Store an asset with playlist to allow load pulse data properly on replay whole playlist
     */
    masterAsset: AssetModel | null = null;

    /**
     * Indicates if ads are currently playing
     */
    adPlaying: boolean = false;

    /**
     * Ad position: preroll|midroll|postroll
     */
    adPosition: string | null = null;

    /**
     * Ad Breaks - stores the list of all ad breaks
     */
    adBreaks: jwplayer.AdBreak[] = [];

    /**
     * Indicates the user is dragging the progress-bar knob.
     *
     * Set by the skin via setScrubbing(). Internal API - deliberately absent
     * from types/index.d.ts and the README, so it is not part of the published
     * contract.
     */
    scrubbing: boolean = false;

    playlistItemId?: number;

    constructor(config?: ConfigModel) {
        super();
        this.config = config!;
    }

    initialize() {
        this.player = jwplayer(this.config.get('node')!);
        this.trigger('initialize');
    }

    async setup({ error }: { error?: unknown } = {}) {
        this.initialize();

        // player could not be setup
        if (!this.player || !(this.player as { setup?: unknown }).setup) {
            console.error(
                `SVP Player initialization error. DOM ${this.config.get(
                    'node',
                )} not found`,
            );
            return;
        }

        await this.getConfig().then((config) => {
            // speedup lookup
            const { player } = this;

            if (!player || !config) {
                return;
            }

            if (error) {
                (config as { autostart?: boolean | string }).autostart = false;
            }

            // load youbora only for non-mock streams and 1% traffic
            let youboraRatio = Math.random() < 0.01;
            player.setup(config);

            svpSkin(this);
            preventAdSeeking(this);

            // set 1% of views for muted autoplayed videos
            const jwConfig = config as { autostart?: boolean | string };
            if (
                player.getMute() === true &&
                (jwConfig.autostart === true ||
                    jwConfig.autostart === 'viewable')
            ) {
                youboraRatio = Math.random() < 0.005;
            }

            if (
                this.stream &&
                !this.stream.isMock() &&
                !this.config.isPreviewMode() &&
                (youboraRatio ||
                    this.stream.hasAccess() ||
                    youboraSdk.forceLoad)
            ) {
                youboraSdk.initialize(this.player!, {
                    accountCode: 'schibsted',
                    enableAnalytics: true,
                });
            }
            logger('JW').log('config', config);

            // forward native JW events without changing their behaviour
            utils.each(
                [
                    'seek',
                    'displayClick',
                    'captionsChanged',
                    'adPause',
                    'adPlay',
                    'autostartNotAllowed',
                    'adClick',
                    'fullscreen',
                    'volume',
                    'mute',
                    'nextShown',
                    'nextAutoAdvance',
                    'nextClick',
                    'relatedReady',
                    'playlistComplete',
                    'playbackRateChanged',
                    'cast',
                    'float',
                ],
                this.forwardEvent.bind(this),
            );

            this.listenTo(player, 'error setupError', this.onError);
            this.listenTo(player, 'playlistItem', this.onPlaylistItemLoad);

            this.listenTo(player, 'play', this.onPlay);
            this.listenTo(player, 'pause', this.onPause);
            this.listenTo(player, 'complete', this.onComplete);
            this.listenTo(
                player,
                'ready',
                this.onReady.bind(this, {
                    canAutoplay: autoplay.canAutoplay(config),
                }),
            );

            this.listenTo(player, 'mute', () => {
                this.adjustAudioVolumeOnMobile();
            });

            this.listenTo(player, 'time', this.onTime);
            this.listenTo(player, 'viewable', this.onViewable);
            this.listenTo(
                player,
                'autostartNotAllowed',
                this.onAutostartNotAllowed,
            );

            this.listenTo(player, 'meta', this.onMeta);

            this.listenTo(
                player,
                'adsManager',
                ({
                    adsManager,
                }: {
                    adsManager: {
                        addEventListener: (
                            eventName: google.ima.AdEvent.Type,
                            handler: (ev: unknown) => void,
                        ) => void;
                    };
                }) => {
                    adsManager.addEventListener(
                        google.ima.AdEvent.Type.IMPRESSION,
                        onAdEventImpression,
                    );
                },
            );
            this.listenTo(
                player,
                'metadataCueParsed',
                this.onMetadataCueParsed,
            );

            this.listenTo(player, 'seek', ({ offset }: jwplayer.SeekParam) => {
                this.currentTime = offset;
            });
            this.listenTo(player, 'seeked', () => {
                this.trigger('seeked', this.currentTime);
                // clearing has to occur after seekend event as getCurrentTime needs it
                this.currentTime = null;
            });

            this.once('initialPlay', () => {
                this.listenToOnce(player, 'captionsList', () => {
                    this.trigger('captionsList', this.getCaptionsList());
                });
            });

            this.on('initialPlay', () => {
                this.isStopped = false;
            });

            this.listenTo(
                player,
                'adImpression',
                (data: jwplayer.AdImpressionData) => {
                    if (data?.adposition) {
                        this.adPosition = getAdPosition(data.adposition);
                    }

                    if (this.adPlaying === false) {
                        this.trigger('adSlotStart', {
                            position: this.adPosition,
                            response: data && data.response,
                            meta: data,
                        });

                        this.adPlaying = true;
                    }

                    this.trigger('adStarted', {
                        position: this.adPosition,
                        response: data && data.response,
                        meta: data,
                        aspectRatio: deriveAspectRatioFromAd(data),
                    });

                    if (data && data.response) {
                        this.trigger('adData', parseAdData(data));
                    }
                },
            );

            this.listenTo(
                player,
                'adError',
                (data: { adposition?: 'pre' | 'mid' | 'post' }) => {
                    if (data) {
                        if (data.adposition) {
                            this.adPosition = getAdPosition(data.adposition);
                        }
                        this.trigger('adError', data);
                    }
                },
            );

            this.listenTo(player, 'adTime', (data: jwplayer.AdTimeParam) => {
                if (data.position && data.duration) {
                    this.trigger('adProgress', data.position, data.duration);
                }
            });

            this.on('adSlotStart', () => {
                this.listenToOnce(player, 'adBreakEnd', () => {
                    if (this.adPlaying === true) {
                        this.trigger('adSlotComplete', {
                            position: this.adPosition,
                        });

                        this.adPlaying = false;
                    }
                });
            });

            this.listenTo(player, 'adSkipped', () => {
                this.trigger('adSkipped');
            });

            this.listenTo(player, 'adComplete', () => {
                this.trigger('adFinished');
            });

            this.listenTo(
                player,
                'adSchedule',
                (data: jwplayer.AdScheduleParam) => {
                    this.adBreaks = data.adbreaks || [];
                },
            );

            this.listenTo(
                player,
                'adBreakEnd',
                ({ offset }: { offset: number }) => {
                    // find the next ad with the distance less or equal 1s to the current one
                    const nextAd = this.adBreaks.find(
                        (ad) => ad.offset > offset && ad.offset - offset <= 1,
                    );
                    if (nextAd) {
                        // workaround: using setTimeout to make seek work
                        setTimeout(() => this.player!.seek(nextAd.offset));
                    }
                },
            );

            this.listenTo(
                player,
                'adLoadedXML',
                (data: { xml?: XMLDocument }) => {
                    if (data && data.xml) {
                        this.trigger('adLoadedXML', data.xml);
                    }
                },
            );

            this.isInitialized = true;

            logger('JW').log((log: (...args: unknown[]) => void) => {
                this.listenTo(
                    player,
                    'all',
                    (event: string, ...rest: unknown[]) => {
                        if (['bufferChange'].indexOf(event) < 0) {
                            log(event, rest);
                        }
                    },
                );
            });

            this.trigger('setup');

            if (adBlockDetected) {
                this.player!.trigger('adBlock');
            }
        });
    }

    async playNext(
        stream: StreamModel,
        { disableAutoplay }: { disableAutoplay: boolean },
    ) {
        this.stream = stream;

        this.complete('playNext');

        if (!this.isInitialized) {
            await this.setup();
        } else {
            await this.getConfig(stream).then((config) => {
                if (!config) {
                    return;
                }
                this.player!.load(
                    (config as { playlist: jwplayer.PlaylistItem[] }).playlist,
                );

                if (disableAutoplay) {
                    return;
                }

                // stream is not available yet, display countdown
                if (stream.getTimeToStart() < 0) {
                    // enable autoplay if stream is not available
                    this.player!.play(true);
                } else {
                    this.player!.stop();
                }
            });
        }
    }

    setMasterAsset(asset: AssetModel | null = null) {
        this.masterAsset = asset;
    }

    /**
     * Checks if the current playlist contains a given asset
     */
    isAssetFromPlaylist(assetId: number): boolean {
        return (
            this.masterAsset != null &&
            (this.masterAsset.getId() === assetId ||
                this.masterAsset
                    .get('playlist')
                    .some(
                        (assetData: { id: number }) => assetData.id === assetId,
                    ))
        );
    }

    /**
     * Immediately completes currently playing stream
     */
    complete(_reason?: string) {
        this.adPlaying = false;

        // trigger complete event only when stream is not finished to prevent double 'complete' event triggering
        if (
            this.getCurrentTime() > 0 &&
            this.getCurrentTime() !== this.getDuration()
        ) {
            this.trigger('complete');
        }
    }

    /**
     * Get DOM Node where player is inserted
     */
    getContainer(): HTMLElement {
        return this.player!.getContainer();
    }

    getPlayer() {
        return this.player;
    }

    getVolume(): number {
        return this.player!.getVolume();
    }

    setVolume(volume: number) {
        this.player!.setVolume(volume);
    }

    getPlaybackRate(): number {
        return this.player!.getPlaybackRate();
    }

    setPlaybackRate(rate: number) {
        this.player!.setPlaybackRate(rate);
    }

    getMute(): boolean {
        return this.player!.getMute();
    }

    setMute(value?: boolean) {
        this.player!.setMute(value);
    }

    seek(time: number) {
        this.player!.seek(time);
    }

    /**
     * Get player config. Method is asynchronous due to loading info from api
     */
    getConfig(stream?: StreamModel | null) {
        const nextStream = stream ?? this.getStream();

        return this.config
            .getJwConfig(nextStream!)
            .then(skinLoader)
            .catch((reason: unknown) => {
                if (typeof reason === 'number') {
                    this.trigger('error', {
                        code: reason,
                        message: statusCode(reason),
                    });

                    return;
                }

                this.trigger('error', reason);
            });
    }

    setStream(stream: StreamModel) {
        this.stream = stream;
    }

    getStream(): StreamModel | null {
        return this.stream;
    }

    isAdPlaying(): boolean {
        return this.adPlaying;
    }

    isScrubbing(): boolean {
        return this.scrubbing;
    }

    /**
     * Marks the start/end of a progress-bar drag gesture.
     *
     * Idempotent: repeated calls with the same value are ignored, so
     * consumers see exactly one scrubStart / scrubEnd per gesture.
     */
    setScrubbing(value: boolean) {
        if (this.scrubbing === value) {
            return;
        }

        this.scrubbing = value;
        this.trigger(value ? 'scrubStart' : 'scrubEnd');
    }

    /**
     * Load given stream
     * Perform all required checks before stream playback
     */
    loadStream(): Promise<void> {
        return new Promise((resolve) => {
            // for lazy loaded player
            if (!this.isInitialized) {
                this.setup();
                this.player!.on('ready', () => resolve());
            } else {
                resolve();
            }
        });
    }

    /**
     * Play stream
     */
    play() {
        // ensure stream is loaded properly
        this.loadStream().then(() => this.player!.play());
    }

    pause(force?: boolean) {
        this.player!.pause(force);
    }

    async stop() {
        // playlistitemId is set when first frame is shown
        // stop shouldn't change stream when it was not played
        if (this.isStopped === false) {
            this.trigger('complete', 'stop');

            const config = await this.getConfig();

            if (config) {
                this.player!.load(
                    (config as { playlist: jwplayer.PlaylistItem[] }).playlist,
                );
            }
            this.player!.stop();

            // reset playlist item to allow replaying this stream
            this.playlistItem = null;

            this.isStopped = true;
        }
    }

    /**
     * Destroy the player instance, reset DOM, clean up listeners
     */
    remove() {
        this.trigger('remove');
        this.off();

        if (this.player) {
            this.pause(true);

            this.player.remove();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this.player as any).off();
        }
    }

    /**
     * Get stream duration
     */
    getDuration(): number {
        const duration = this.player!.getDuration();

        if (duration < 0) {
            return -duration;
        }

        return duration;
    }

    /**
     * Get current playback time
     */
    getCurrentTime(): number {
        let { currentTime } = this;

        if (currentTime) {
            return currentTime;
        }

        // player not initialized (error/geoblock)
        // return 0
        if (!this.player) {
            return 0;
        }

        currentTime = this.player.getPosition();

        if (currentTime < 0) {
            return this.getDuration() + currentTime;
        }

        return this.player.getPosition();
    }

    getState(): string {
        if (this.isAdPlaying()) {
            return 'adPlaying';
        }

        return this.player!.getState();
    }

    getFloating(): boolean {
        return this.player!.getFloating();
    }

    setFloating(shouldFloat: boolean) {
        return this.player!.setFloating(shouldFloat);
    }

    getAdBlock(): boolean {
        return adBlockDetected;
    }

    /**
     * Get poster for current stream
     * Poster may be changed in config
     */
    getPoster(stream: StreamModel, container: HTMLElement): string | null {
        return this.config.getPoster(stream, container);
    }

    /**
     * Get list of captions extended by data from api
     */
    getCaptionsList() {
        const playerCaptions = this.player!.getCaptionsList();
        const captions = this.getStream()!.getCaptions();

        // merge player captions data with result from api
        utils.each(playerCaptions, (caption) => {
            utils.each(captions, (item) => {
                if (caption.id === item.url) {
                    caption.language = item.language;
                    caption.default = item.default;
                }
            });
        });

        return playerCaptions;
    }

    /**
     * Set current captions
     * @param {number|string} index string is language key, number is index in array
     */
    setCurrentCaptions(index: number | string) {
        let newIndex: number | string = index || 0;

        if (!utils.isNumber(index)) {
            utils.each(
                this.getCaptionsList(),
                (caption: { language?: string }, captionsIndex: number) => {
                    if (caption && caption.language === index) {
                        newIndex = captionsIndex;
                    }
                },
            );

            // fallback to off in case index is not found in captions array
            if (!utils.isNumber(newIndex)) {
                newIndex = 0;
            }
        }

        // set captions in player
        this.player!.setCurrentCaptions(newIndex as number);
    }

    /**
     * Proxy for getCurrentCaptions from JW
     */
    getCurrentCaptions() {
        if (!this.player) {
            console.warn(
                'Method getCurrentCaptions called before player has been initialised',
            );
            return null;
        }

        return this.player.getCurrentCaptions();
    }

    /**
     * Handle all errors from player
     */
    onError(data: jwplayer.ErrorParam) {
        logger('SVP').error(data);
        const { code } = data;
        const message = data.message ? data.message.toString() : '';

        /**
         * Some errors do not prevent playback to play
         * thus they should be omitted
         */
        const skipErrors = [
            'Captions failed to load', // could not load
            'Casting failed to load', // chrome disabled
        ];

        // skip errors
        if (skipErrors.indexOf(message) > -1) {
            return;
        }

        this.trigger('error', {
            message,
            code,
        });
    }

    /**
     * Event triggered before first stream play
     * Useful for statistics
     */
    onPlaylistItemLoad(playlistItem: jwplayer.PlaylistItemParam) {
        // cleanup any previous beforePlay event (playnext after countdown or error)
        this.stopListening(this.player, 'beforePlay');

        // trigger this event every time new playlist item is loaded
        this.listenToOnce(this.player, 'beforePlay', () => {
            if (this.stream!.getTimeToStart() < 0) {
                this.trigger('initialPlay');
            }
        });

        // ads should not if stream has future start time
        // attach event only for secure streams
        if (this.stream!.isSecure()) {
            // reload playlist only if token expired
            // play method do this out of the box
            this.listenToOnce(this.player, 'displayClick', () => {
                if (
                    !this.config.hasValidToken() &&
                    this.player!.getState() !== 'playing'
                ) {
                    this.play();
                }
            });
        }

        this.trigger('playlistItem', playlistItem.item);

        // clear ad playing flag for current content
        // important when changing stream while adslot is playing
        this.adPlaying = false;

        this.listenToOnce(this.player, 'firstFrame', () => {
            this.playlistItemId = playlistItem.item.id;
            this.onFirstFrameLoad(this.playlistItemId);
        });

        this.playlistItem = playlistItem.item;

        // for streams which end time is not equal to video length
        const playbackEndTime = this.stream!.getPlaybackTime('end');

        // attach playback listener
        if (playbackEndTime != null && playbackEndTime > 0) {
            this.onPlaybackEndAvailable(playbackEndTime);
        }
    }

    onTime({ position, duration }: jwplayer.TimeParam) {
        if (position < 0) {
            this.trigger('time', -(duration - position), -duration);
        } else {
            this.trigger('time', position, duration);
        }
    }

    /**
     * Triggered on first frame of content playback - after ads
     */
    onFirstFrameLoad(playlistItemId: number) {
        // first play of stream
        const mediaType = this.stream!.get('mediaType');
        const startTime = this.stream!.getPlaybackTime('begin') || 0;

        /**
         * Remove "jw-flag-media-audio" class when media type is video
         * but was not recognized properly to fix poster image issues
         */
        if (mediaType === 'video') {
            utils.removeClass(this.getContainer(), 'jw-flag-media-audio');
        }

        this.once('time', () => {
            // check if currently set video id is the same as played one
            // it can happen when playnext is called before assetPlay
            if (playlistItemId === this.stream!.getId()) {
                this.trigger('assetPlay', startTime);
            }
        });
    }

    onViewable(eventData: jwplayer.ViewableParam) {
        const { viewable } = eventData;

        this.trigger('viewable', {
            viewable,
        });
    }

    onAutostartNotAllowed(data: jwplayer.AutostartNotAllowedParam) {
        const { reason } = data;

        if (reason === 'autoplayDisabled') {
            this.player!.setMute(false);
        }
    }

    async createMidroll(slotDuration: number, startTime: number | 'NOW') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const midroll = (appnexus as any).createMidroll({
            tag: await this.config.getLiveMidrollTag(slotDuration),
            startTime,
        }) as {
            setTime: (t: number) => void;
            onReady: (cb: () => void) => void;
            onEnter: (cb: (tag: string) => void) => void;
        };

        const onTimeChange = () =>
            midroll.setTime(this.player!.getCurrentTime());

        midroll.onReady(() => {
            this.on('time', onTimeChange);
            // cleanup when stream has changed or finished
            this.on('complete', () => this.off('time', onTimeChange));
        });

        midroll.onEnter((adTag: string) => {
            const position = -Math.abs(this.player!.getPosition());
            this.off('time', onTimeChange);

            this.once('adSlotComplete', () => {
                this.listenToOnce(this.player, 'providerFirstFrame', () => {
                    this.player!.seek(position);
                });
            });

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this.player as any).loadAdTag(adTag);
        });
    }

    /**
     * Handling midrolls with scte35 tags in HLS live
     */
    onMetadataCueParsed(data: jwplayer.MetadataCueParsedParam) {
        if (!this.stream!.isLive()) {
            return;
        }

        const { tag, content, start } = data.metadata || {};

        if (tag === 'EXT-X-CUE-OUT') {
            this.createMidroll(parseInt(content!, 10), start!);
        } else if (tag === 'EXT-X-CUE-IN') {
            this.player!.skipAd();
        }
    }

    /**
     * Handling midrolls in RTMP live streams
     */
    onMeta(data: {
        metadata?: { TXXX?: { insertAd?: string; cancelAd?: string } };
    }) {
        if (!this.stream!.isLive()) {
            return;
        }

        const params = data.metadata?.TXXX;

        if (params) {
            // Workaround for live midrolls SCTE-35 tags in Safari
            // @see https://schibsted.ghe.com/svp/platform/issues/328
            if (params.insertAd && device.isSafari()) {
                const duration = parseInt(params.insertAd, 10);
                this.createMidroll(duration, 'NOW');
            }

            // Workaround for live midrolls SCTE-35 tags in Safari
            // @see https://schibsted.ghe.com/svp/platform/issues/328
            if (params.cancelAd === 'now' && device.isSafari()) {
                this.player!.skipAd();
            }
        }
    }

    onPlay(data: jwplayer.PlayParam) {
        this.trigger('play', {
            playReason: data.playReason,
        });
    }

    onPause(data: jwplayer.PauseParam) {
        utils.removeClass(this.getContainer(), 'jw-state-buffering');

        this.trigger('pause', data);
    }

    /**
     * Correct event flow for end of streaming
     */
    onComplete() {
        // wait until postroll will finish
        this.trigger('complete');
    }

    /**
     * Forward JW Events without any change as they fit our needs
     */
    private forwardEvent(event: string) {
        this.listenTo(this.player, event, this.trigger.bind(this, event));
    }

    /**
     * Adjusts player volume to 100% for audio assets under mobile devices or text to speech assets
     */
    private adjustAudioVolumeOnMobile() {
        const isMobileAudio = device.isMobile() && this.stream!.isAudio();
        if (isMobileAudio && !this.player!.getMute()) {
            this.player!.setVolume(100);
        }
    }

    /**
     * Extended ready event with info about device autoplay ability
     */
    private onReady(options: { canAutoplay: boolean }, eventData: object) {
        this.trigger('ready', utils.extend(options, eventData));

        this.adjustAudioVolumeOnMobile();
    }

    /**
     * Initialize playback end method
     * Clear after each completion of stream or playbackEnd reached
     */
    private onPlaybackEndAvailable(playbackEnd: number) {
        const onTime = (position: number) => {
            if (position > playbackEnd) {
                this.trigger('playbackEnd', Math.round(position));
            }
        };

        this.on('time', onTime);

        this.once('playbackEnd complete', () => {
            this.off('time', onTime);
        });
    }
}

export default PlayerModel;
